import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "react-bootstrap";
import CalenderIcon from "../../../images/calender-icon.svg";
import discount from "../../../images/discount-icon.svg";
import ListIcon8 from "../../../images/promo-icon.svg";
import CouponIcon from "../../../images/coupon-icon.svg";
import { Col, Form, FormGroup, FormLabel, Row } from "react-bootstrap";
import SearchIcon from "../../../images/search-icon.svg";
import { FormControl, InputGroup } from "react-bootstrap";
import axios from 'axios';
import apiConfig from "../apiConfig.ts";
const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` })
    };
};

const CouponInfoItem = ({ icon, label, value, valueStyle = {} }) => (
    <Col md={4}>
        <div className="d-flex align-items-center mb-3">
            <img src={icon} alt={label} />
            <p className="my-auto ms-2">
                {label}: <strong style={valueStyle}>{value}</strong>
            </p>
        </div>
    </Col>
);

const ReferralDiscountDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [couponData, setCouponData] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterBy, setFilterBy] = useState("");

    const couponInfoItems = [
        {
            icon: discount,
            label: "Coupon Name",
            value: couponData?.couponName || 'N/A'
        },
        {
            icon: CouponIcon,
            label: "Coupon Code",
            value: couponData?.couponCode || 'N/A'
        },
        {
            icon: discount,
            label: "Discount",
            value: couponData?.discount || 'N/A'
        },
        {
            icon: ListIcon8,
            label: "Usage",
            value: `${couponData?.usedCount || 0}/${couponData?.usersLimit || 'N/A'}`
        },
        {
            icon: CalenderIcon,
            label: "Start Date",
            value: couponData?.startDate ? new Date(couponData.startDate).toLocaleDateString('en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            }) : 'N/A'
        },
        {
            icon: CalenderIcon,
            label: "End Date",
            value: couponData?.endDate ? new Date(couponData.endDate).toLocaleDateString('en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            }) : 'N/A'
        },
        {
            icon: discount,
            label: "Status",
            value: couponData?.status || (couponData?.active ? 'Active' : 'Inactive'),
            valueStyle: {
                color: (couponData?.status?.toLowerCase() === 'active' || couponData?.active)
                    ? "rgba(3, 192, 25, 1)"
                    : (couponData?.status?.toLowerCase() === 'expired')
                        ? "rgba(255, 159, 67, 1)"
                        : "rgba(220, 53, 69, 1)"
            }
        }
    ];

    const fetchGiftTransactions = async (couponCode) => {
        try {
            const response = await axios.get(`${apiConfig.apiBaseURL}/promo/gift-transactions/${couponCode}`, {
                headers: getAuthHeaders()
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    };

    useEffect(() => {
        const loadData = async () => {
            if (!id) return;

            try {
                setLoading(true);
                setError(null);

                // Directly use the id as the coupon code and fetch transactions
                // The API returns both transactions and promoCodeInfo
                const transactionsData = await fetchGiftTransactions(id);

                // Check if we have data in the response
                if (transactionsData.data?.promoCodeInfo) {
                    const promoInfo = transactionsData.data.promoCodeInfo;
                    // Map API response fields to component expected fields
                    setCouponData({
                        couponCode: promoInfo.code,
                        couponName: promoInfo.title,
                        discount: promoInfo.discountType === 'percentage'
                            ? `${promoInfo.discount}%`
                            : `$${promoInfo.discount}`,
                        usedCount: promoInfo.usageCount,
                        usersLimit: promoInfo.usersAllowed,
                        startDate: promoInfo.startDate,
                        endDate: promoInfo.endDate,
                        status: promoInfo.status,
                        active: promoInfo.status === 'active'
                    });

                    // Set transactions from data.transactions
                    setTransactions(transactionsData.data.transactions || []);
                } else {
                    throw new Error('Failed to load promo code information');
                }

            } catch (err) {
                // console.error('Error loading data:', err);
                setError(err.message || 'Failed to load data');
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [id]);

    const filteredTransactions = transactions.filter(transaction => {
        if (!searchTerm) return true;
        return (
            transaction.orderId?.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
            transaction.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            transaction.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            transaction.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            transaction.phoneNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            transaction.phone?.toLowerCase().includes(searchTerm.toLowerCase())
        );
    });

    if (loading) {
        return (
            <div className="main-body">
                <div className="text-center py-5">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading coupon details...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="main-body">
                <div className="alert alert-danger" role="alert">
                    <h4 className="alert-heading">Error!</h4>
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="main-body">
                {/* Breadcrumb / Back Button */}
                <div className="d-flex align-items-center mb-4">
                    <Button
                        variant="link"
                        className="text-decoration-none p-0 fw-bold d-flex align-items-center gap-2 btn btn-link me-2 btn btn-link"
                        onClick={() => navigate('/referral-discount')}
                        style={{ color: '#3957a0' }}
                    >
                        ← Back
                    </Button>
                    <nav aria-label="breadcrumb">
                        <ol className="breadcrumb mb-0">
                            <li className="breadcrumb-item">
                                <span className="text-muted">Discount Codes</span>
                            </li>
                            <li className="breadcrumb-item active">
                                {couponData?.couponCode || id}
                            </li>
                        </ol>
                    </nav>
                </div>

                <section className="detail-list">
                    <div className="detail-list-in">
                        <aside className="row">
                            <aside className="col-12">
                                <div className='billing-list'>
                                    <Row>
                                        {couponInfoItems.map((item, index) => (
                                            <CouponInfoItem
                                                key={index}
                                                icon={item.icon}
                                                label={item.label}
                                                value={item.value}
                                                valueStyle={item.valueStyle}
                                            />
                                        ))}
                                    </Row>
                                </div>
                            </aside>
                        </aside>
                    </div>
                </section>
                <div className="main-table">
                    <div className="referral-discount">
                        <Row
                            className="mb-3 justify-content-between main-table-filter add-new-strip"
                            style={{ alignItems: "center" }}
                        >
                            <Col md={6} lg={5} xl={3} className="select-field table-select">
                                <FormGroup className="form-group w-100 px-1">
                                    <FormLabel>Filter by</FormLabel>
                                    <Form.Select value={filterBy} onChange={(e) => setFilterBy(e.target.value)}>
                                        <option value="">All</option>
                                        <option value="orderId">Order ID</option>
                                        <option value="name">Name</option>
                                        <option value="email">Email</option>
                                    </Form.Select>
                                </FormGroup>
                            </Col>
                            <Col md={6} lg={6} xl={4} className="physical-strap-block-right">
                                <FormGroup className="form-group w-100 px-1 search-box">
                                    <InputGroup>
                                        <FormControl
                                            type="text"
                                            placeholder="Search transactions..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                        <InputGroup.Text>
                                            <img src={SearchIcon} alt="" />
                                        </InputGroup.Text>
                                    </InputGroup>
                                </FormGroup>
                            </Col>
                        </Row>
                    </div>
                    <div className="table-responsive mt-3">
                        <table className="table table-bordered">
                            <thead>
                                <tr>
                                    <th>Order ID</th>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Phone</th>
                                    <th>Order Date</th>
                                    <th>Amount</th>
                                    <th>Discount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredTransactions.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="text-center">
                                            {transactions.length === 0 ? 'No transactions found for this coupon' : 'No transactions match your search'}
                                        </td>
                                    </tr>
                                ) : (
                                    filteredTransactions.map((transaction, index) => (
                                        <tr key={transaction.id || index}>
                                            <td>{transaction.orderId || 'N/A'}</td>
                                            <td>{transaction.userName || transaction.name || 'N/A'}</td>
                                            <td>{transaction.email || 'N/A'}</td>
                                            <td>{transaction.phoneNumber || transaction.phone || 'N/A'}</td>
                                            <td>{transaction.orderDate ? new Date(transaction.orderDate).toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: '2-digit',
                                                day: '2-digit'
                                            }) : 'N/A'}</td>
                                            <td>${transaction.amount || 0}</td>
                                            <td>{transaction.discountPercentage ? `${transaction.discountPercentage}%` : `$${transaction.discountAmount || transaction.discount || 0}`}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>

                    </div>
                </div>
            </div>
        </>
    )
}
export default ReferralDiscountDetails